<script setup>
import { ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import DocView from '@addictivegroup/addictive-docview/DocView.vue'
import { TEXT_LIMIT, decodeText } from '@/lib/filePreview.js'
import UiSpinner from '@/components/ui/UiSpinner.vue'
import PdfPreview from './PdfPreview.vue'

/**
 * A file shown in the panel (D-223), as lib/filePreview says it can be: a PDF page by page, a
 * picture, a film or a recording, a text file as it is (its start, when large), an Office
 * document (Word, Excel, PowerPoint, CSV) drawn by addictive-docview — searchable, zoomable,
 * full screen, cells to select and copy. `src` is where the file is read from (a blob of the
 * device's copy, or Core's address), so an Office file opens offline too.
 */
const props = defineProps({
  file: { type: Object, required: true }, // { uuid, name, mime, size }
  kind: { type: String, required: true },
  src: { type: String, required: true },
})
const { locale } = useI18n()
const text = ref('')
const cut = ref(false)
const loading = ref(false)
const failed = ref(false)

async function load() {
  text.value = ''
  failed.value = false
  if (props.kind !== 'text') return
  loading.value = true
  try {
    const response = await fetch(props.src, { credentials: 'include' })
    if (!response.ok) throw new Error(String(response.status))
    const buffer = await response.arrayBuffer()
    cut.value = buffer.byteLength > TEXT_LIMIT
    text.value = decodeText(buffer.slice(0, TEXT_LIMIT))
  } catch {
    failed.value = true
  } finally {
    loading.value = false
  }
}
watch(() => [props.src, props.kind], load, { immediate: true })
</script>

<template>
  <div data-testid="file-preview" :data-kind="kind">
    <div v-if="loading" class="py-12 text-center"><UiSpinner size="lg" /></div>
    <p v-else-if="failed" class="py-8 text-center text-sm text-muted">{{ $t('attachments.noPreview') }}</p>
    <PdfPreview v-else-if="kind === 'pdf'" v-private :src="src" />
    <img v-else-if="kind === 'image'" v-private :src="src" :alt="file.name" class="mx-auto max-h-[75vh] rounded object-contain" />
    <video v-else-if="kind === 'video'" :src="src" controls autoplay playsinline class="mx-auto max-h-[75vh] w-full rounded bg-black"></video>
    <audio v-else-if="kind === 'audio'" :src="src" controls autoplay class="w-full"></audio>
    <template v-else-if="kind === 'text'">
      <pre v-private class="max-h-[75vh] overflow-auto whitespace-pre-wrap break-words rounded-lg bg-[var(--surface-2)] p-3 font-mono text-xs leading-relaxed" data-testid="file-preview-text">{{ text }}</pre>
      <p v-if="cut" class="mt-2 text-xs text-muted">{{ $t('attachments.textCut') }}</p>
    </template>
    <DocView v-else-if="kind === 'office'" :src="src" :name="file.name" :mime="file.mime" :locale="locale === 'en' ? 'en-GB' : 'pl-PL'" class="h-[calc(100dvh-11rem)] sm:h-[calc(90vh-11rem)]" />
  </div>
</template>
